import { Skeleton, Stack } from '@mui/material';
import { VerticalTabs } from 'common/ui/VerticalTabs';
import { useCandidateProfileStore } from 'modules/candidate-profile/stores/useCandidateProfileStore';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetConversationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { TextBlock } from 'common/ui/TextBlock';
import { ItemSeparator } from 'common/ui/ItemSeparator';
import { getCandidateProfileConversationSeparatorItems } from 'modules/candidate-profile/utils/separator-items/getCandidateProfileConversationSeparatorItems';
import { ConversationDetails } from 'modules/conversations/components/ConversationDetails';
import { EmptyState } from 'system';
import { CONVERSATION_ID_PARAM } from 'modules/candidate-profile/constants';

const CandidateProfileConversationsSkeleton: React.FC = () => {
  return (
    <Stack gap="30px">
      <Skeleton variant="rectangular" width="500px" height={69} />
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="10px"
        width="100%"
      >
        <Skeleton variant="rectangular" width="500px" height={32} />
      </Stack>
    </Stack>
  );
};

export const CandidateProfileConversationsTabs: React.FC = () => {
  const { conversations } = useCandidateProfileStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedConversationId = searchParams.get(CONVERSATION_ID_PARAM);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const isSelectionInList = conversations?.some(
      (conversation) => conversation.id === selectedConversationId
    );
    if (isSelectionInList) return;
    const linkedConversation = conversations?.find(
      (conversation) => conversation.id === linkedConversationId
    );
    setSelectedConversationId(
      linkedConversation?.id ?? conversations?.[0]?.id ?? null
    );
  }, [conversations]);

  const { data } = useGetConversationQuery({
    variables: {
      id: selectedConversationId ?? ''
    },
    skip: !selectedConversationId
  });

  const conversation = extractQueryGetItem(data);

  const jobDescription = conversation?.campaign?.jobDescriptionParsed;

  const separatorItems =
    getCandidateProfileConversationSeparatorItems(conversation);

  if (conversations?.length === 0) {
    return <EmptyState type="no-records" />;
  }

  const selectedIndex =
    conversations?.findIndex(
      (conversation) => conversation.id === selectedConversationId
    ) ?? 0;

  return (
    <Stack direction="row">
      <VerticalTabs
        containerSx={{
          height: '949px'
        }}
        defaultSelectedIndex={selectedIndex >= 0 ? selectedIndex : 0}
        items={conversations?.map((conversation) => ({
          id: conversation.id,
          title:
            conversation.campaign?.jobDescriptionParsed?.jobTitle ||
            conversation.campaign?.title ||
            '',
          subtitle:
            conversation.campaign?.jobDescriptionParsed?.employerName ?? '',
          description:
            conversation.campaign?.jobDescriptionParsed?.location ?? ''
        }))}
        onTabClick={(item) => {
          setSelectedConversationId(item.id);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set(CONVERSATION_ID_PARAM, item.id);
          setSearchParams(newSearchParams, { replace: true });
        }}
      />
      <Stack flex={1} gap="30px">
        {selectedConversationId && !conversation && (
          <CandidateProfileConversationsSkeleton />
        )}
        {conversation && (
          <>
            <TextBlock
              variant="section"
              title={
                conversation.campaign?.title ||
                jobDescription?.jobTitle ||
                'Campaign'
              }
              subtitle={jobDescription?.location ?? ''}
            />
            <Stack direction="row" justifyContent="space-between">
              <ItemSeparator items={separatorItems} />
            </Stack>
            <ConversationDetails
              conversationId={conversation.id}
              shouldHideApplicationStage
              shouldHideCandidateDetails
              shouldHideScreeningInProgress
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};
